import React, { useId } from 'react';
import { RadioGroup, Box } from '@amzn/awsui-components-console';
import { SearchMode, SEARCH_MODE } from '../SearchPage';
import styles from './SearchModeSelector.module.scss';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface SearchModeSelectorProps {
  readonly mode: SearchMode;
  readonly onModeChange: (mode: SearchMode) => void;
}

/**
 * Rendered as a leading inline label rather than a `FormField` `label`, which Cloudscape stacks
 * ABOVE its control; the two options read as a sentence with the label beside them.
 */
const SEARCH_IN_LABEL_ID = 'search-mode-label';

/**
 * What the query text is matched against: the source string or its translations.
 * The first row of the search panel.
 *
 * Split out of the former `SearchTypeToggle`, which also carried "Exact match" —
 * that control now sits BELOW the query input (see `ExactMatchToggle`), so the two
 * no longer share a row and have nothing else in common: this one picks the corpus,
 * the other one picks the matching rule.
 */
const SearchModeSelector: React.FC<SearchModeSelectorProps> = ({
  mode,
  onModeChange,
}) => {
  const { t } = useTranslation();
  // Per-instance: a module-level id would point two selectors at the first label.
  const labelId = useId();

  return (
    <div className={styles.container} data-testid="search-mode-selector">
      {/* `Box`'s own `id` prop is deprecated, so the id goes on an inner span. */}
      <Box variant="awsui-key-label">
        <span id={labelId}>{t(SEARCH_IN_LABEL_ID)}</span>
      </Box>
      {/*
        `ariaLabelledby` rather than `ariaLabel`: the label is on screen, so
        pointing the group at it keeps the announced name and the visible text the
        same string by construction — a duplicated `ariaLabel` could drift from it.
      */}
      <RadioGroup
        value={mode}
        onChange={({ detail }) => onModeChange(detail.value as SearchMode)}
        ariaLabelledby={labelId}
        direction="horizontal"
        items={[
          { value: SEARCH_MODE.SOURCE, label: t('search-mode-source') },
          {
            value: SEARCH_MODE.TRANSLATION,
            label: t('search-mode-translation'),
          },
        ]}
      />
    </div>
  );
};

export default SearchModeSelector;
